import {Box, Card, CircularProgress, Typography} from "@mui/material";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import ActiveAndClosedButton from "./activeAndClosedButton.tsx";
import Filters from "./filter/index.tsx";
import TicketListHeader from "./ticketListHeader.tsx";
import NoTicketsError from "./noTicketsError.tsx";
import TicketsCard from "./ticketsCard.tsx";
import {AppDispatch, RootState} from "@store/store.tsx";
import {
    fetchTickets,
    fetchTotalTickets,
    updateTicketStatus,
} from "@store/ticketListSlice.tsx";
import ResolvedModal from "../../../Chat/components/resolvedModal.tsx";
import ResolvedDrawer from "../../../Chat/components/resolvedDrawer.tsx";
import AddUserModal from "@common/components/AddUser/addUserModal.tsx";


const TicketList: React.FC = () => {

    const [resolvedDrawer, setResolvedDrawer] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>("");
    const [searchValue, setSearchValue] = useState("");
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [addUserModal, setAddUserModal] = useState<boolean>(false);
    const [sortByValue, setSortByValue] = useState("updated_at");
    const [sortByOrder, setSortByOrder] = useState("desc");
    const [hideResolved, setHideResolved] = useState(false);
    const [updateSearchValue, setUpdateSearchValue] = useState("");
    const [ticketIdAndStatus, setTicketIdAndStatus] = useState<{
        ticketId: number;
        status: number;
        tag: string;
        orgId: number | undefined;
    }>({ticketId: 0, status: 0, tag: "", orgId: undefined});
    const open = Boolean(anchorEl);
    const scrollRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const {customStatuses} = useSelector((state: RootState) => state.config);
    const ticketData = useSelector(
        (state: RootState) => state.tickets
    );
    const {active, closed, loading, retrigger, organizations, error} = ticketData
    const [searchParams] = useSearchParams();

    const status =
        (searchParams.get("status") as "active" | "closed") || "active";

    const activeTicketData: any = React.useMemo(() => {
        if (status !== 'active')
            return undefined
        return {
            data: [
                ...ticketData.active.live_perfusion.data,
                ...ticketData.active.post_perfusion.data,
                ...ticketData.active.general.data
            ],
            ticketData
        }
    }, [ticketData, status]);
    const closedTicketData: any = React.useMemo(() => {
        if (status !== 'closed')
            return undefined
        return {
            data: [
                ...ticketData.closed.live_perfusion.data,
                ...ticketData.closed.post_perfusion.data,
                ...ticketData.closed.general.data,
            ],
            ticketData
        }
    }, [ticketData, status]);

    useEffect(() => {
        if (status) {
            navigate(`/?status=${status}`);
        }
    }, [status]);

    const handleClick = (
        event: React.MouseEvent<HTMLElement>,
        label: string,
        data?: any
    ) => {
        if (label === "resolved") {
            const customStatus = customStatuses.find((status) => status.endUserLabel === "Resolved")
            setAnchorEl(event.currentTarget);
            let tagValue = "general";
            if (data.tags.includes("live_perfusion")) {
                tagValue = "live_perfusion";
            } else if (data.tags.includes("post_perfusion")) {
                tagValue = "post_perfusion";
            }
            setTicketIdAndStatus({
                ticketId: data?.id,
                status: customStatus?.id,
                tag: tagValue,
                orgId: data.organizationId,
            });
        }
        if (data.customStatusId == "25271088415249") {
            setHideResolved(true);
        } else {
            setHideResolved(false);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTicketNavigation = (ticketId?: number) => {
        navigate(`/chat/${ticketId}?status=${status}`);
    };

    React.useEffect(() => {
        dispatch(
            fetchTotalTickets({
                type: status,
                status: filterValue,
                query: updateSearchValue,
            })
        );
    }, [status, updateSearchValue, filterValue])

    useEffect(() => {
        dispatch(
            fetchTickets({
                type: status,
                query: updateSearchValue,
                reset: true,
                status: filterValue,
                sortBy: sortByValue,
                order: sortByOrder,
            })
        );
    }, [
        status,
        updateSearchValue,
        filterValue,
        sortByValue,
        sortByOrder,
    ]);

    useEffect(() => {
        if (retrigger && (error === null)) {
            dispatch(
                fetchTickets({
                    type: status,
                    query: updateSearchValue,
                    status: filterValue,
                    sortBy: sortByValue,
                    order: sortByOrder,
                })
            );
        }
    }, [retrigger]);

    const onIntersection = useCallback(
        ([entry]: any) => {
            const hasMore =
                status === "active"
                    ? activeTicketData.ticketData.active?.totalCount && activeTicketData.data?.length !== activeTicketData.ticketData.active?.totalCount
                    : closedTicketData.ticketData.closed?.totalCount && closedTicketData.ticketData.closed?.totalCount !== closedTicketData.data?.length;
            const ticketLoading = status === "active" ? activeTicketData.ticketData.loading : closedTicketData.ticketData.loading
            if (entry.isIntersecting && ticketLoading !== status && hasMore) {
                dispatch(
                    fetchTickets({
                        type: status,
                        query: updateSearchValue,
                        status: filterValue,
                        sortBy: sortByValue,
                        order: sortByOrder,
                    })
                );
            }
        },
        [status, updateSearchValue, activeTicketData, closedTicketData]
    );

    useEffect(() => {
        const currentRef = scrollRef.current;
        if (!currentRef) return;
        const observer = new IntersectionObserver(onIntersection, {
            threshold: 0.1,
        });
        observer.observe(currentRef);
        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, [onIntersection]);


    const handleActiveTickets = (status: "active" | "closed") => {
        navigate(`/?status=${status}`);
    };

    const handleTicketAction = (action: string) => {
        if (action === "resolved") {
            setResolvedDrawer(true);
        } else {
            setAddUserModal(true);
        }
    };

    const handleTicketResolvedAction = () => {
        const payload = {
            customTicketStatusId: ticketIdAndStatus.status,
            tag: ticketIdAndStatus.tag,
            statusName: "solved",
        };
        if (ticketIdAndStatus) {
            dispatch(
                updateTicketStatus({ticketId: ticketIdAndStatus.ticketId, payload})
            );
            setAnchorEl(null);
            setResolvedDrawer(false);
        }
    };


    return (
        <>
            <ActiveAndClosedButton
                handleActiveTickets={handleActiveTickets}
                activeTickets={status}
            />
            <Card>
                <Box component="div" className="pt-2 px-2 ">
                    <Box
                        className="flex flex-row justify-between border-dotted border-b-2 md:border-[#0000004D] border-[#666666]">
                        <TicketListHeader
                            activeTotalCount={active?.totalCount}
                            closedTotalCount={closed?.totalCount}
                            activeTickets={status}
                        />

                        <Box className="hidden md:flex">
                            <Filters
                                setFilterValue={setFilterValue}
                                setSearchValue={setSearchValue}
                                setSortByValue={setSortByValue}
                                setSortByOrder={setSortByOrder}
                                setUpdateSearchValue={setUpdateSearchValue}
                                updateSearchValue={updateSearchValue}
                                filterValue={filterValue}
                                searchValue={searchValue}
                                sortByValue={sortByValue}
                                sortByOrder={sortByOrder}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className="flex  flex-col md:hidden">
                    <Filters
                        setFilterValue={setFilterValue}
                        setSearchValue={setSearchValue}
                        setSortByValue={setSortByValue}
                        setSortByOrder={setSortByOrder}
                        setUpdateSearchValue={setUpdateSearchValue}
                        updateSearchValue={updateSearchValue}
                        filterValue={filterValue}
                        searchValue={searchValue}
                        sortByValue={sortByValue}
                        sortByOrder={sortByOrder}
                    />
                </Box>

                {(activeTicketData?.data?.length === 0 || closedTicketData?.data?.length === 0) &&
                    loading !== status && !error && <NoTicketsError/>}

                {error ? (
                    <Box sx={{
                        height: {
                            xs: "calc(100vh - 414px)",
                            sm: "calc(100vh - 301px)",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    }}
                    >
                        <Typography style={{fontSize: 14}}>{error}</Typography>
                    </Box>
                ) : (
                    <Box
                        className="grid grid-cols-1 divide-y"
                        sx={{
                            maxHeight: {
                                xs: "calc(100vh - 462px)",
                                sm: "calc(100vh - 325px)",
                            },
                            overflowY: "auto",
                        }}
                    >
                        <TicketsCard
                            activeTickets={status}
                            ticketsData={
                                status === "active" ? activeTicketData.data : closedTicketData.data
                            }
                            customStatuses={customStatuses}
                            organizations={organizations}
                            handleClick={handleClick}
                            handleClose={handleClose}
                            handleTicketAction={handleTicketAction}
                            open={open}
                            anchorEl={anchorEl}
                            handleTicketNavigation={handleTicketNavigation}
                            hideResolved={hideResolved}
                        />

                        {loading === status && (
                            <Box className="h-[5px] py-8 flex justify-center items-center">
                                <CircularProgress size="20px"/>
                            </Box>
                        )}
                        <Box ref={scrollRef}></Box>
                    </Box>
                )}
            </Card>
            <ResolvedModal
                resolvedDrawer={resolvedDrawer}
                setResolvedDrawer={setResolvedDrawer}
                handleTicketResolvedAction={handleTicketResolvedAction}
            />
            <ResolvedDrawer
                resolvedDrawer={resolvedDrawer}
                setResolvedDrawer={setResolvedDrawer}
                handleTicketResolvedAction={handleTicketResolvedAction}
            />
            <AddUserModal
                addUserModal={addUserModal}
                setAddUserModal={setAddUserModal}
                ticketId={ticketIdAndStatus.ticketId}
            />
        </>
    );
};

export default TicketList;
 